import { useMemo } from "react";
import Select from "react-select";
import { t } from "../../../i18n";

export type AutomaticReportSendMode = "YES_WHEN_CLOSED" | "YES_WHEN_VALIDATED" | "NO";

const AVAILABLE_MODES: AutomaticReportSendMode[] = ["YES_WHEN_VALIDATED", "NO"];

const MODE_STATUS_COLORS: Partial<Record<AutomaticReportSendMode, string>> = {
  YES_WHEN_VALIDATED: "#2DBA43",
  YES_WHEN_CLOSED: "#6B7280",
};

const MODE_TRANSLATION_KEYS: Record<AutomaticReportSendMode, string> = {
  YES_WHEN_CLOSED: "client:form:fields:automaticReportSend:options:yesWhenClosed",
  YES_WHEN_VALIDATED: "client:form:fields:automaticReportSend:options:yesWhenValidated",
  NO: "client:form:fields:automaticReportSend:options:no"
};

export function normalizeAutomaticReportSendMode(
  mode?: AutomaticReportSendMode | null
): AutomaticReportSendMode | null {
  if (!mode) {
    return null;
  }
  if (AVAILABLE_MODES.includes(mode)) {
    return mode;
  }
  if (mode === "YES_WHEN_CLOSED") {
    return "YES_WHEN_VALIDATED";
  }
  return null;
}

/**
 * The API has no notion of a send *mode*: CustomerRequest/CustomerDetailedResponse only carry the
 * boolean sendInterventionReportEmail, and any extra field is discarded on PUT. The mode is a
 * display-only projection of that boolean -- it must never be persisted alongside it, or a stale
 * client-side enum can outrank the value the server actually returned.
 */
export function resolveAutomaticReportSendMode(
  sendInterventionReportEmail?: boolean | null
): AutomaticReportSendMode | null {
  if (sendInterventionReportEmail === false) {
    return "NO";
  }
  if (sendInterventionReportEmail === true) {
    return "YES_WHEN_VALIDATED";
  }
  return null;
}

export function mapAutomaticReportSendToApi(mode: AutomaticReportSendMode | null): boolean | null {
  if (mode === "YES_WHEN_CLOSED" || mode === "YES_WHEN_VALIDATED") {
    return true;
  }
  if (mode === "NO") {
    return false;
  }
  return null;
}

export function AutomaticReportSendLabel({ mode }: { mode: AutomaticReportSendMode }) {
  const label = t(MODE_TRANSLATION_KEYS[mode]);
  const statusColor = MODE_STATUS_COLORS[mode];

  if (!statusColor) {
    return <span>{label}</span>;
  }

  const parts = label.split(" ");
  const statusWord = parts[parts.length - 1];
  const prefix = parts.slice(0, -1).join(" ");

  return (
    <span>
      {prefix}{prefix ? " " : ""}
      <span style={{ color: statusColor, fontWeight: 600 }}>{statusWord}</span>
    </span>
  );
}

export default function AutomaticReportSendSelect({
  value,
  onChange,
  className = ""
}: {
  value?: AutomaticReportSendMode | null;
  onChange: (mode: AutomaticReportSendMode | null) => void;
  className?: string;
}) {
  const options = useMemo(
    () => AVAILABLE_MODES.map((mode) => ({
      value: mode,
      label: t(MODE_TRANSLATION_KEYS[mode])
    })),
    []
  );

  const normalizedValue = normalizeAutomaticReportSendMode(value);
  const selectedOption = normalizedValue
    ? options.find((option) => option.value === normalizedValue) ?? null
    : null;

  return (
    <Select
      options={options}
      value={selectedOption}
      isClearable={false}
      menuPosition="fixed"
      formatOptionLabel={(option) => (
        <AutomaticReportSendLabel mode={option.value as AutomaticReportSendMode} />
      )}
      onChange={(option) => onChange(option ? option.value as AutomaticReportSendMode : null)}
      className={`w-full mr-2 ${className}`}
      classNamePrefix="react-select"
      placeholder={t("client:form:fields:automaticReportSend:placeholder")}
      theme={(theme) => ({
        ...theme,
        colors: {
          ...theme.colors,
          primary: "rgb(14 165 233)"
        }
      })}
    />
  );
}
